import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BirthdayIcon } from "@/components/birthday-icon";
import { Link } from "react-router-dom";
import { addDays, addMinutes, addWeeks, format, startOfWeek } from "date-fns";
import { CopyIcon, SaveIcon } from "lucide-react";
import type {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventDropArg,
  EventInput,
} from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import type {
  DateClickArg,
  EventReceiveArg,
  EventResizeDoneArg,
} from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { MobileCalendarToolbar } from "@/components/mobile-calendar-toolbar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  createSession,
  deleteSession,
  getCurrentUserRole,
  getResourceCategories,
  getConfirmedBookingCountsBySession,
  getCourses,
  getSessionRegistrationSettings,
  getSessionCancellationSettings,
  getTrainerProfiles,
  getResources,
  getSessionBookings,
  getSessions,
  applyTemplateToWeek,
  previewSessionMove,
  saveWeekAsTemplate,
  updateSession,
} from "@/lib/api";
import {
  MOBILE_CALENDAR_BREAKPOINT,
  getCalendarToolbarTitle,
  getInitialCalendarWindowWidth,
  getResponsiveCalendarLayout,
} from "@/lib/calendar-layout";
import {
  formatDateTime,
  fromInputDateTime,
  getSessionRegistrationUntil,
  getSessionCancellationUntil,
  toInputDateTime,
} from "@/lib/date";
import { getErrorMessage } from "@/lib/error-messages";
import { queryKeys } from "@/lib/query-keys";
import type {
  Course,
  CourseSession,
  SessionMoveConflict,
  SessionMoveConflictReason,
} from "@/types/domain";

import "./admin-sessions-page.css";
import { ResourceSelectionHeader } from "@/components/resource-selection-header";

const START_TIME = "06:00:00";
const END_TIME = "22:00:00";
const SLOT_DURATION = "00:15:00";
const DEFAULT_SCROLL_TIME = "08:00:00";
const FALLBACK_MAX_PARTICIPANTS = "20";
const DEFAULT_COURSE_COLOR = "#38BDF8";
const CANCELLED_EVENT_COLOR = "#ef4444";
const SESSION_MOVE_REASON_LABELS: Record<SessionMoveConflictReason, string> = {
  weekly_limit:
    "Wochenkontingent in der Zielwoche überschritten – Buchung bleibt bestehen.",
  subscription_inactive:
    "Abo derzeit nicht aktiv (z. B. Zahlung offen) – Buchung bleibt bestehen.",
  after_subscription_end:
    "Termin liegt nach Abo-Ende – Buchung wird storniert, Guthabenanteil wird erstattet.",
};

type PendingSessionMove = {
  session: CourseSession;
  startsAt: Date;
  isPast: boolean;
  conflicts: SessionMoveConflict[];
  proceed: () => void | Promise<void>;
  revert: () => void;
};
const TEMPLATE_SIDEBAR_BREAKPOINT = 1280;

const sessionSchema = z
  .object({
    course_id: z.string().min(1, "Bitte einen Kurs auswählen."),
    trainer_id: z.string(),
    resource_id: z.string(),
    starts_at: z.string().min(1, "Bitte einen Startzeitpunkt eingeben."),
    max_participants: z
      .string()
      .refine((value) => Number.isFinite(Number(value)) && Number(value) > 0, {
        message: "Bitte eine gültige maximale Teilnehmerzahl eingeben.",
      }),
    min_participants: z
      .string()
      .refine(
        (value) =>
          value.trim() === "" ||
          (Number.isFinite(Number(value)) && Number(value) > 0),
        "Bitte eine gültige Mindest-Teilnehmerzahl eingeben oder leer lassen.",
      ),
    is_cancelled: z.boolean(),
    registration_until: z.string(),
    cancellation_until: z.string(),
  })
  .superRefine((values, context) => {
    const startsAtIso = fromInputDateTime(values.starts_at);
    if (!startsAtIso) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Bitte einen gültigen Startzeitpunkt eingeben.",
        path: ["starts_at"],
      });
    }

    if (
      values.registration_until.trim() !== "" &&
      !fromInputDateTime(values.registration_until)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Bitte einen gültigen Zeitpunkt für die Anmeldefrist eingeben.",
        path: ["registration_until"],
      });
    }

    if (
      values.cancellation_until.trim() !== "" &&
      !fromInputDateTime(values.cancellation_until)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Bitte einen gültigen Zeitpunkt für die Stornierungsfrist eingeben.",
        path: ["cancellation_until"],
      });
    }

    if (values.min_participants.trim() !== "") {
      const min = Number(values.min_participants);
      const max = Number(values.max_participants);
      if (Number.isFinite(min) && Number.isFinite(max) && min > max) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Die Mindest-Teilnehmerzahl darf die maximale Teilnehmerzahl nicht übersteigen.",
          path: ["min_participants"],
        });
      }
    }

    if (startsAtIso && values.registration_until.trim() !== "") {
      const registrationUntilIso = fromInputDateTime(values.registration_until);
      if (
        registrationUntilIso &&
        new Date(registrationUntilIso) > new Date(startsAtIso)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Die Anmeldefrist darf nicht nach dem Terminstart liegen.",
          path: ["registration_until"],
        });
      }
    }

    if (startsAtIso && values.cancellation_until.trim() !== "") {
      const cancellationUntilIso = fromInputDateTime(values.cancellation_until);
      if (
        cancellationUntilIso &&
        new Date(cancellationUntilIso) > new Date(startsAtIso)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Die Stornierungsfrist darf nicht nach dem Terminstart liegen.",
          path: ["cancellation_until"],
        });
      }
    }
  });

type SessionForm = z.infer<typeof sessionSchema>;

type CalendarRange = {
  from: string;
  to: string;
};

type SessionsPageMode = "admin" | "trainer";

type AdminSessionsPageProps = {
  mode?: SessionsPageMode;
};

const emptyForm: SessionForm = {
  course_id: "",
  trainer_id: "",
  resource_id: "none",
  starts_at: "",
  max_participants: FALLBACK_MAX_PARTICIPANTS,
  min_participants: "",
  is_cancelled: false,
  registration_until: "",
  cancellation_until: "",
};

function getCalendarWeekRange(date: Date): CalendarRange {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end = addWeeks(start, 1);

  return {
    from: format(start, 'yyyy-MM-dd'),
    to: format(end, 'yyyy-MM-dd'),
  };
}

function getInitialCalendarRange(date: Date): CalendarRange {
  const { calendarView } = getResponsiveCalendarLayout(
    getInitialCalendarWindowWidth(),
  );

  if (calendarView === "timeGridDay") {
    return {
      from: format(date, "yyyy-MM-dd"),
      to: format(addDays(date, 1), "yyyy-MM-dd"),
    };
  }

  return getCalendarWeekRange(date);
}

function isHexColor(value: string | null | undefined): value is string {
  return Boolean(value && /^#[0-9A-Fa-f]{6}$/.test(value));
}

function toEventFillColor(hex: string): string {
  return hex;
}

function textColorForHex(hex: string): string {
  const cleanHex = hex.replace("#", "");
  const r = Number.parseInt(cleanHex.slice(0, 2), 16);
  const g = Number.parseInt(cleanHex.slice(2, 4), 16);
  const b = Number.parseInt(cleanHex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#111827" : "#ffffff";
}

function getActiveAssignedTrainerIdsForCourse(
  courseId: string,
  courses: Course[],
  activeTrainerIds: Set<string>,
): string[] {
  const course = courses.find((entry) => entry.id === courseId);
  if (!course) {
    return [];
  }

  return course.trainers
    .map((trainer) => trainer.id)
    .filter((trainerId) => activeTrainerIds.has(trainerId));
}

function getAssignedTrainerIdsForCourse(
  courseId: string,
  courses: Course[],
): string[] {
  const course = courses.find((entry) => entry.id === courseId);
  if (!course) {
    return [];
  }

  return course.trainers.map((trainer) => trainer.id);
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    target.isContentEditable
  );
}

export function AdminSessionsPage({ mode = "admin" }: AdminSessionsPageProps) {
  const isTrainerMode = mode === "trainer";
  const queryClient = useQueryClient();
  const calendarRef = useRef<FullCalendar | null>(null);
  const templatesRef = useRef<HTMLDivElement | null>(null);
  const previousStartsAtRef = useRef<string | null>(null);

  const [open, setOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<CourseSession | null>(
    null,
  );
  const sessionForm = useForm<SessionForm>({
    resolver: zodResolver(sessionSchema),
    defaultValues: emptyForm,
  });
  const [activeTemplateCourseId, setActiveTemplateCourseId] =
    useState<string>("");
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<CourseSession | null>(
    null,
  );
  const [calendarActionError, setCalendarActionError] = useState<string | null>(
    null,
  );
  const [dataUpdateCount, setDataUpdateCount] = useState(0);
  const [saveTemplateConfirmOpen, setSaveTemplateConfirmOpen] = useState(false);
  const [applyTemplateConfirmOpen, setApplyTemplateConfirmOpen] = useState(false);
  const [overwriteTemplate, setOverwriteTemplate] = useState(false);
  // Values of a save that sets the session to cancelled, waiting for confirmation.
  const [pendingCancellation, setPendingCancellation] = useState<SessionForm | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingSessionMove | null>(
    null,
  );
  const [visibleRange, setVisibleRange] = useState<CalendarRange>(() =>
    getInitialCalendarRange(new Date()),
  );
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(
    null,
  );
  const [windowWidth, setWindowWidth] = useState(getInitialCalendarWindowWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { calendarView, visibleDays } = useMemo(
    () => getResponsiveCalendarLayout(windowWidth),
    [windowWidth],
  );
  const [currentCalendarView, setCurrentCalendarView] = useState(calendarView);
  const [calendarTitle, setCalendarTitle] = useState("");
  const showMobileToolbar = windowWidth < MOBILE_CALENDAR_BREAKPOINT;
  const showTemplateSidebar = windowWidth >= TEMPLATE_SIDEBAR_BREAKPOINT;
  const calendarViews = [
    { label: "Tag", value: "timeGridDay" },
    { label: "Woche", value: "timeGridWeek" },
  ];

  useEffect(() => {
    setCurrentCalendarView(calendarView);
  }, [calendarView]);

  const [watchedCourseId, watchedStartsAt] = useWatch({ control: sessionForm.control, name: ["course_id", "starts_at"] });

  const sessionsQuery = useQuery({
    queryKey: queryKeys.sessions(visibleRange.from, visibleRange.to),
    queryFn: () => getSessions(visibleRange),
  });
  const profileQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    enabled: isTrainerMode,
  });

  const coursesQuery = useQuery({
    queryKey: queryKeys.courses,
    queryFn: () => getCourses(true),
  });
  const registrationSettingsQuery = useQuery({
    queryKey: queryKeys.sessionRegistrationSettings,
    queryFn: getSessionRegistrationSettings,
  });
  const cancellationSettingsQuery = useQuery({
    queryKey: queryKeys.sessionCancellationSettings,
    queryFn: getSessionCancellationSettings,
  });
  const trainersQuery = useQuery({
    queryKey: queryKeys.trainerProfiles,
    queryFn: getTrainerProfiles,
    enabled: !isTrainerMode,
  });
  const bookingCountsQuery = useQuery({
    queryKey: queryKeys.sessionBookingCounts,
    queryFn: getConfirmedBookingCountsBySession,
  });
  const sessionBookingsQuery = useQuery({
    queryKey: queryKeys.sessionBookings(editingSession?.id ?? ""),
    queryFn: () => getSessionBookings(editingSession!.id),
    enabled: open && Boolean(editingSession),
  });
  const confirmedSessionBookings = useMemo(
    () =>
      (sessionBookingsQuery.data ?? [])
        .filter((booking) => booking.status === "confirmed")
        .sort(
          (a, b) =>
            a.full_name.localeCompare(b.full_name, "de", {
              sensitivity: "base",
            }) ||
            a.email.localeCompare(b.email, "de", { sensitivity: "base" }),
        ),
    [sessionBookingsQuery.data],
  );

  const resourceCategoriesQuery = useQuery({
    queryKey: ["resource-categories"],
    queryFn: getResourceCategories,
  });

  const resourcesQuery = useQuery({
    queryKey: ["resources-all"],
    queryFn: () => getResources(),
  });

  const courses = useMemo(() => coursesQuery.data ?? [], [coursesQuery.data]);
  const registrationLeadHours = registrationSettingsQuery.data?.hours ?? 0;
  const cancellationLeadHours = cancellationSettingsQuery.data?.hours ?? 12;
  const trainerProfileId = profileQuery.data?.id ?? null;
  const manageableCourseIds = useMemo(() => {
    if (!isTrainerMode || !trainerProfileId) {
      return new Set<string>();
    }

    return new Set(
      courses
        .filter((course) =>
          course.trainers.some((trainer) => trainer.id === trainerProfileId),
        )
        .map((course) => course.id),
    );
  }, [courses, isTrainerMode, trainerProfileId]);
  const formSelectableCourses = useMemo(() => {
    if (!isTrainerMode) {
      return courses;
    }

    const editingCourseId = editingSession?.course_id ?? null;

    return courses.filter(
      (course) =>
        manageableCourseIds.has(course.id) || course.id === editingCourseId,
    );
  }, [courses, editingSession, isTrainerMode, manageableCourseIds]);
  const templateCourses = useMemo(
    () =>
      [...formSelectableCourses].sort((a, b) =>
        a.name.localeCompare(b.name, "de", { sensitivity: "base" }),
      ),
    [formSelectableCourses],
  );
  const trainers = useMemo(
    () => trainersQuery.data ?? [],
    [trainersQuery.data],
  );
  const rawSessions = useMemo(
    () => sessionsQuery.data ?? [],
    [sessionsQuery.data],
  );
  const sessions = useMemo(() => {
    if (!selectedResourceId) return rawSessions;
    return rawSessions.filter((s) => s.resource_id === selectedResourceId);
  }, [rawSessions, selectedResourceId]);
  const bookingCounts = useMemo(
    () => bookingCountsQuery.data ?? {},
    [bookingCountsQuery.data],
  );
  const activeTrainerIds = useMemo(
    () => new Set(trainers.map((trainer) => trainer.id)),
    [trainers],
  );
  const resourceCategories = useMemo(
    () => resourceCategoriesQuery.data ?? [],
    [resourceCategoriesQuery.data],
  );
  const resources = useMemo(
    () => resourcesQuery.data ?? [],
    [resourcesQuery.data],
  );

  const canCreateForCourse = useCallback(
    (courseId: string) => {
      if (!isTrainerMode) {
        return true;
      }
      return manageableCourseIds.has(courseId);
    },
    [isTrainerMode, manageableCourseIds],
  );
  const canManageSession = useCallback(
    (session: Pick<CourseSession, "trainer_id">) => {
      if (!isTrainerMode) {
        return true;
      }
      return (
        Boolean(trainerProfileId) && session.trainer_id === trainerProfileId
      );
    },
    [isTrainerMode, trainerProfileId],
  );

  const categoriesWithResources = useMemo(
    () =>
      resourceCategories.map((cat) => ({
        ...cat,
        resources: resources.filter((r) => r.category_id === cat.id),
      })),
    [resourceCategories, resources],
  );

  const resolvedTemplateCourseId =
    activeTemplateCourseId || templateCourses[0]?.id || "";

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === watchedCourseId) ?? null,
    [courses, watchedCourseId],
  );

  const selectableTrainers = trainers;

  const calculatedEndsAt = useMemo(() => {
    if (!selectedCourse || !watchedStartsAt) {
      return null;
    }

    const dt = fromInputDateTime(watchedStartsAt);
    if (!dt) {
      return null;
    }

    const startsAt = new Date(dt);
    return addMinutes(startsAt, selectedCourse.duration_minutes).toISOString();
  }, [watchedStartsAt, selectedCourse]);

  const calculatedRegistrationUntil = useMemo(() => {
    const startsAtIso = fromInputDateTime(watchedStartsAt);
    return getSessionRegistrationUntil(startsAtIso, registrationLeadHours);
  }, [registrationLeadHours, watchedStartsAt]);

  const calculatedCancellationUntil = useMemo(() => {
    const startsAtIso = fromInputDateTime(watchedStartsAt);
    return getSessionCancellationUntil(startsAtIso, cancellationLeadHours);
  }, [cancellationLeadHours, watchedStartsAt]);

  useEffect(() => {
    if (!open) {
      previousStartsAtRef.current = null;
      return;
    }

    const previousStartsAt = previousStartsAtRef.current;
    previousStartsAtRef.current = watchedStartsAt;

    if (!previousStartsAt || previousStartsAt === watchedStartsAt) {
      return;
    }

    const nextStartsAtIso = fromInputDateTime(watchedStartsAt);
    if (!nextStartsAtIso) {
      return;
    }

    if (calculatedRegistrationUntil) {
      sessionForm.setValue(
        "registration_until",
        toInputDateTime(calculatedRegistrationUntil),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }

    if (calculatedCancellationUntil) {
      sessionForm.setValue(
        "cancellation_until",
        toInputDateTime(calculatedCancellationUntil),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
  }, [
    calculatedCancellationUntil,
    calculatedRegistrationUntil,
    open,
    sessionForm,
    watchedStartsAt,
  ]);

  useEffect(() => {
    if (!templatesRef.current) {
      return;
    }

    const draggable = new Draggable(templatesRef.current, {
      itemSelector: "[data-template-course-id]",
      eventData: (eventEl) => {
        const title =
          eventEl.getAttribute("data-template-title") ?? "Kursblock";
        const courseId = eventEl.getAttribute("data-template-course-id") ?? "";
        const duration = Number(
          eventEl.getAttribute("data-template-duration") ?? "60",
        );

        return {
          title,
          duration: { minutes: duration },
          extendedProps: {
            templateCourseId: courseId,
          },
        };
      },
    });

    return () => draggable.destroy();
  }, [courses, showTemplateSidebar]);

  const openCreateDialog = (startsAt: Date, courseId: string) => {
    if (!canCreateForCourse(courseId)) {
      setCalendarActionError(
        "Du kannst nur Termine für deine eigenen Kurse erstellen.",
      );
      return;
    }

    const course = courses.find((entry) => entry.id === courseId);
    const assignedTrainerIds = getAssignedTrainerIdsForCourse(
      courseId,
      courses,
    );
    const activeAssignedTrainerIds = getActiveAssignedTrainerIdsForCourse(
      courseId,
      courses,
      activeTrainerIds,
    );

    setEditingSession(null);
    sessionForm.reset({
      course_id: courseId,
      trainer_id: isTrainerMode
        ? ""
        : assignedTrainerIds.length === 1 &&
          activeAssignedTrainerIds.length === 1
          ? activeAssignedTrainerIds[0]
          : "",
      resource_id: selectedResourceId ?? "none",
      starts_at: toInputDateTime(startsAt.toISOString()),
      max_participants: String(
        course?.default_max_participants ?? Number(FALLBACK_MAX_PARTICIPANTS),
      ),
      min_participants:
        course?.default_min_participants == null
          ? ""
          : String(course.default_min_participants),
      is_cancelled: false,
      registration_until: toInputDateTime(
        getSessionRegistrationUntil(startsAt.toISOString(), registrationLeadHours),
      ),
      cancellation_until: toInputDateTime(
        getSessionCancellationUntil(startsAt.toISOString(), cancellationLeadHours),
      ),
    });
    saveMutation.reset();
    setOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async (values: SessionForm) => {
      const selected = courses.find((course) => course.id === values.course_id);

      if (!selected) {
        throw new Error("Bitte einen Kurs auswählen.");
      }
      if (isTrainerMode) {
        if (editingSession && !canManageSession(editingSession)) {
          throw new Error("Du kannst diesen Termin nicht bearbeiten.");
        }
        if (!editingSession && !canCreateForCourse(values.course_id)) {
          throw new Error(
            "Du kannst nur Termine für deine eigenen Kurse erstellen.",
          );
        }
      }

      const normalizedTrainerId = values.trainer_id.trim();
      if (!isTrainerMode && normalizedTrainerId && !activeTrainerIds.has(normalizedTrainerId)) {
        throw new Error(
          "Bitte einen aktiven Trainer auswählen oder das Feld leer lassen.",
        );
      }

      const startsAtIso = fromInputDateTime(values.starts_at);
      if (!startsAtIso) {
        throw new Error("Bitte einen gültigen Startzeitpunkt eingeben.");
      }

      const endsAtIso = addMinutes(
        new Date(startsAtIso),
        selected.duration_minutes,
      ).toISOString();
      const minParticipants =
        values.min_participants.trim() === ""
          ? null
          : Number(values.min_participants);

      const payload = {
        course_id: values.course_id,
        trainer_id: isTrainerMode
          ? (editingSession?.trainer_id ?? null)
          : normalizedTrainerId || null,
        resource_id:
          values.resource_id === "none" || !values.resource_id
            ? null
            : values.resource_id,
        starts_at: startsAtIso,
        ends_at: endsAtIso,
        max_participants: Number(values.max_participants),
        min_participants: minParticipants,
        is_cancelled: values.is_cancelled,
        registration_until: fromInputDateTime(values.registration_until),
        cancellation_until: fromInputDateTime(values.cancellation_until),
      };

      if (editingSession) {
        await updateSession(editingSession.id, payload);
        return;
      }

      await createSession(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionsBase });
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionBookingCounts });
      setDataUpdateCount((c) => c + 1);
      setOpen(false);
      setEditingSession(null);
      sessionForm.reset(emptyForm);
      setCalendarActionError(null);
      toast.success(
        editingSession
          ? "Termin erfolgreich aktualisiert."
          : "Termin erfolgreich erstellt.",
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  // A changed start time is checked for affected members first; the move
  // itself is never blocked.
  const confirmSessionMove = async ({
    session,
    startsAt,
    proceed,
    revert,
    onError,
  }: {
    session: CourseSession;
    startsAt: Date;
    proceed: () => void | Promise<void>;
    revert: () => void;
    onError: (message: string) => void;
  }) => {
    if (startsAt.getTime() === new Date(session.starts_at).getTime()) {
      await proceed();
      return;
    }

    let conflicts: SessionMoveConflict[];
    try {
      conflicts = await previewSessionMove(session.id, startsAt.toISOString());
    } catch (error) {
      revert();
      onError(getErrorMessage(error));
      return;
    }

    const isPast = startsAt < new Date();
    if (!isPast && conflicts.length === 0) {
      await proceed();
      return;
    }

    setPendingMove({ session, startsAt, isPast, conflicts, proceed, revert });
  };

  const submitSession = (values: SessionForm) => {
    if (editingSession && values.is_cancelled && !editingSession.is_cancelled) {
      setPendingCancellation(values);
      return;
    }

    const startsAtIso = fromInputDateTime(values.starts_at);
    if (
      !editingSession ||
      !startsAtIso ||
      values.is_cancelled ||
      !canManageSession(editingSession)
    ) {
      saveMutation.mutate(values);
      return;
    }

    void confirmSessionMove({
      session: editingSession,
      startsAt: new Date(startsAtIso),
      proceed: () => saveMutation.mutate(values),
      revert: () => {},
      onError: (message) => toast.error(message),
    });
  };

  const onSubmit = sessionForm.handleSubmit((values) => {
    if (isTrainerMode) {
      if (!editingSession && !canCreateForCourse(values.course_id)) {
        sessionForm.setError("course_id", {
          type: "manual",
          message: "Du kannst nur Termine für deine eigenen Kurse speichern.",
        });
        return;
      }
      submitSession(values);
      return;
    }

    const normalizedTrainerId = values.trainer_id.trim();
    if (!normalizedTrainerId) {
      submitSession(values);
      return;
    }

    if (!activeTrainerIds.has(normalizedTrainerId)) {
      sessionForm.setError("trainer_id", {
        type: "manual",
        message:
          "Bitte einen aktiven Trainer auswählen oder das Feld leer lassen.",
      });
      return;
    }

    submitSession(values);
  });

  const quickUpdateMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Parameters<typeof updateSession>[1];
    }) => updateSession(id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionsBase });
      setCalendarActionError(null);
      toast.success("Termin erfolgreich aktualisiert.");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const quickCreateMutation = useMutation({
    mutationFn: (payload: {
      course_id: string;
      trainer_id: string | null;
      resource_id: string | null;
      starts_at: string;
      ends_at: string;
      max_participants: number;
      min_participants: number | null;
      is_cancelled: boolean;
      registration_until: string | null;
      cancellation_until: string | null;
    }) => createSession(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionsBase });
      setCalendarActionError(null);
      toast.success("Termin erfolgreich erstellt.");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: () => saveWeekAsTemplate(visibleRange.from),
    onSuccess: (count) => {
      toast.success(`${count} Termine erfolgreich als Template gespeichert.`);
      setSaveTemplateConfirmOpen(false);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const applyTemplateMutation = useMutation({
    mutationFn: () => applyTemplateToWeek(visibleRange.from, overwriteTemplate),
    onSuccess: async (count) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sessionsBase }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sessionBookingCounts }),
        // Force immediate refetch of active session queries to ensure UI updates
        queryClient.refetchQueries({ queryKey: queryKeys.sessionsBase, type: 'active' }),
      ]);
      setDataUpdateCount((c) => c + 1);
      toast.success(`${count} Termine erfolgreich angewendet.`);
      setApplyTemplateConfirmOpen(false);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSession(id),
    onSuccess: async (_, deletedSessionId) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessionsBase });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.sessionBookingCounts,
      });
      setDeleteConfirmOpen(false);
      setSessionToDelete(null);
      setOpen(false);
      setEditingSession(null);
      sessionForm.reset(emptyForm);
      setCalendarActionError(null);
      setDataUpdateCount((c) => c + 1);
      setSelectedSessionId((prev) => (prev === deletedSessionId ? null : prev));
      toast.success("Termin erfolgreich gelöscht.");
    },
    onError: (error) => {
      setCalendarActionError(getErrorMessage(error));
      toast.error(getErrorMessage(error));
    },
  });

  const openDeleteConfirm = useCallback(
    (session: CourseSession) => {
      if (!canManageSession(session)) {
        setCalendarActionError(
          "Dieser Termin ist nur lesbar. Bearbeitung ist nur möglich, wenn du als Trainer eingetragen bist.",
        );
        return;
      }
      deleteMutation.reset();
      setCalendarActionError(null);
      setSessionToDelete(session);
      setDeleteConfirmOpen(true);
    },
    [canManageSession, deleteMutation],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Delete") {
        return;
      }

      if (isTypingTarget(event.target)) {
        return;
      }

      if (open || deleteConfirmOpen || deleteMutation.isPending) {
        return;
      }

      if (!selectedSessionId) {
        return;
      }

      const selectedSession = sessions.find(
        (session) => session.id === selectedSessionId,
      );
      if (!selectedSession) {
        return;
      }
      if (!canManageSession(selectedSession)) {
        return;
      }

      event.preventDefault();
      openDeleteConfirm(selectedSession);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    canManageSession,
    deleteConfirmOpen,
    deleteMutation.isPending,
    open,
    openDeleteConfirm,
    selectedSessionId,
    sessions,
  ]);

  const calendarEvents = useMemo<EventInput[]>(
    () =>
      sessions.map((session) => {
        const canManage = canManageSession(session);
        const isLocked = !canManage;

        return {
          ...(session.is_cancelled
            ? {
              backgroundColor: CANCELLED_EVENT_COLOR,
              borderColor: CANCELLED_EVENT_COLOR,
              textColor: "#ffffff",
            }
            : {
              backgroundColor: toEventFillColor(
                isHexColor(session.courses?.color)
                  ? session.courses.color
                  : DEFAULT_COURSE_COLOR,
              ),
              borderColor: isHexColor(session.courses?.color)
                ? session.courses.color
                : DEFAULT_COURSE_COLOR,
              textColor: textColorForHex(
                isHexColor(session.courses?.color)
                  ? session.courses.color
                  : DEFAULT_COURSE_COLOR,
              ),
            }),
          id: session.id,
          title: session.courses?.name ?? "Kurs",
          start: session.starts_at,
          end: session.ends_at,
          editable: !session.is_cancelled && canManage,
          classNames: [
            session.is_cancelled
              ? "motionlab-event-cancelled"
              : "motionlab-event-course",
            selectedSessionId === session.id ? "motionlab-event-selected" : "",
            isLocked ? "motionlab-event-locked" : "",
          ],
          extendedProps: {
            courseId: session.course_id,
            courseColor: isHexColor(session.courses?.color)
              ? session.courses.color
              : DEFAULT_COURSE_COLOR,
            bookedCount: bookingCounts[session.id] ?? 0,
            maxParticipants: session.max_participants,
            isCancelled: session.is_cancelled,
            isLocked,
            trainerName:
              session.trainer?.full_name ?? session.trainer?.email ?? null,
          },
        };
      }),
    [bookingCounts, canManageSession, selectedSessionId, sessions],
  );

  const handleDateClick = (arg: DateClickArg) => {
    setSelectedSessionId(null);

    if (!resolvedTemplateCourseId) {
      setCalendarActionError(
        isTrainerMode
          ? "Wähle links zuerst einen deiner Kurse als Block-Vorlage aus."
          : "Lege zuerst einen Kurs an und wähle links eine Block-Vorlage.",
      );
      return;
    }

    openCreateDialog(arg.date, resolvedTemplateCourseId);
  };

  const handleEventClick = (arg: EventClickArg) => {
    const session = sessions.find((entry) => entry.id === arg.event.id);
    if (!session) {
      return;
    }

    const isDoubleClick = arg.jsEvent.detail >= 2;
    if (!isDoubleClick && selectedSessionId !== session.id) {
      setSelectedSessionId(session.id);
      return;
    }
    if (!canManageSession(session)) {
      setSelectedSessionId(session.id);
      setCalendarActionError(
        "Dieser Termin ist nur lesbar. Bearbeitung ist nur möglich, wenn du als Trainer eingetragen bist.",
      );
      return;
    }

    setSelectedSessionId(session.id);
    setEditingSession(session);
    sessionForm.reset({
      course_id: session.course_id,
      trainer_id: session.trainer_id ?? "",
      resource_id: session.resource_id ?? "none",
      starts_at: toInputDateTime(session.starts_at),
      max_participants: String(session.max_participants),
      min_participants:
        session.min_participants == null
          ? ""
          : String(session.min_participants),
      is_cancelled: session.is_cancelled,
      registration_until: toInputDateTime(session.registration_until),
      cancellation_until: toInputDateTime(session.cancellation_until),
    });
    saveMutation.reset();
    setOpen(true);
  };

  const handleEventReceive = async (arg: EventReceiveArg) => {
    const courseId = String(arg.event.extendedProps.templateCourseId ?? "");
    const startsAt = arg.event.start;
    const course = courses.find((entry) => entry.id === courseId);

    if (!startsAt || !course) {
      arg.revert();
      setCalendarActionError("Der Block konnte nicht angelegt werden.");
      return;
    }

    arg.revert();
    if (!canCreateForCourse(courseId)) {
      setCalendarActionError(
        "Du kannst nur Termine für deine eigenen Kurse erstellen.",
      );
      return;
    }

    if (isTrainerMode) {
      try {
        await quickCreateMutation.mutateAsync({
          course_id: courseId,
          trainer_id: null,
          resource_id: selectedResourceId ?? null,
          starts_at: startsAt.toISOString(),
          ends_at: addMinutes(startsAt, course.duration_minutes).toISOString(),
          max_participants: course.default_max_participants,
          min_participants: course.default_min_participants,
          is_cancelled: false,
          registration_until: getSessionRegistrationUntil(
            startsAt.toISOString(),
            registrationLeadHours,
          ),
          cancellation_until: getSessionCancellationUntil(
            startsAt.toISOString(),
            cancellationLeadHours,
          ),
        });
      } catch (error) {
        setCalendarActionError(getErrorMessage(error));
      }
      return;
    }

    const assignedTrainerIds = getAssignedTrainerIdsForCourse(
      courseId,
      courses,
    );
    const activeAssignedTrainerIds = getActiveAssignedTrainerIdsForCourse(
      courseId,
      courses,
      activeTrainerIds,
    );
    if (
      assignedTrainerIds.length === 1 &&
      activeAssignedTrainerIds.length === 1
    ) {
      try {
        await quickCreateMutation.mutateAsync({
          course_id: courseId,
          trainer_id: isTrainerMode ? null : activeAssignedTrainerIds[0],
          resource_id: selectedResourceId ?? null,
          starts_at: startsAt.toISOString(),
          ends_at: addMinutes(startsAt, course.duration_minutes).toISOString(),
          max_participants: course.default_max_participants,
          min_participants: course.default_min_participants,
          is_cancelled: false,
          registration_until: getSessionRegistrationUntil(
            startsAt.toISOString(),
            registrationLeadHours,
          ),
          cancellation_until: getSessionCancellationUntil(
            startsAt.toISOString(),
            cancellationLeadHours,
          ),
        });
      } catch (error) {
        setCalendarActionError(getErrorMessage(error));
      }

      return;
    }

    openCreateDialog(startsAt, courseId);
    setCalendarActionError(null);
  };

  const handleEventDrop = async (arg: EventDropArg) => {
    const session = sessions.find((entry) => entry.id === arg.event.id);
    const startsAt = arg.event.start;
    const endsAt = arg.event.end;

    if (!session || !startsAt) {
      arg.revert();
      return;
    }
    if (!canManageSession(session)) {
      arg.revert();
      setCalendarActionError(
        "Dieser Termin ist nur lesbar. Bearbeitung ist nur möglich, wenn du als Trainer eingetragen bist.",
      );
      return;
    }

    if (!endsAt || endsAt <= startsAt) {
      arg.revert();
      setCalendarActionError("Ungültiger Zeitraum. Bitte erneut versuchen.");
      return;
    }

    await confirmSessionMove({
      session,
      startsAt,
      proceed: () => executeMoveDrop(session, startsAt, endsAt, arg.revert),
      revert: arg.revert,
      onError: setCalendarActionError,
    });
  };

  const executeMoveDrop = async (
    session: CourseSession,
    startsAt: Date,
    endsAt: Date,
    revert: () => void,
  ) => {
    try {
      await quickUpdateMutation.mutateAsync({
        id: session.id,
        payload: {
          course_id: session.course_id,
          trainer_id: session.trainer_id ?? null,
          resource_id: session.resource_id ?? null,
          starts_at: startsAt.toISOString(),
          ends_at: endsAt.toISOString(),
          max_participants: session.max_participants,
          min_participants: session.min_participants,
          is_cancelled: session.is_cancelled,
          registration_until: getSessionRegistrationUntil(
            startsAt.toISOString(),
            registrationLeadHours,
          ),
          cancellation_until: getSessionCancellationUntil(
            startsAt.toISOString(),
            cancellationLeadHours,
          ),
        },
      });
    } catch (error) {
      revert();
      setCalendarActionError(getErrorMessage(error));
    }
  };

  const handleEventResize = async (arg: EventResizeDoneArg) => {
    const session = sessions.find((entry) => entry.id === arg.event.id);
    const startsAt = arg.event.start;
    const endsAt = arg.event.end;

    if (!session || !startsAt || !endsAt || endsAt <= startsAt) {
      arg.revert();
      setCalendarActionError("Ungültiger Zeitraum. Bitte erneut versuchen.");
      return;
    }
    if (!canManageSession(session)) {
      arg.revert();
      setCalendarActionError(
        "Dieser Termin ist nur lesbar. Bearbeitung ist nur möglich, wenn du als Trainer eingetragen bist.",
      );
      return;
    }

    await confirmSessionMove({
      session,
      startsAt,
      proceed: () => executeMoveDrop(session, startsAt, endsAt, arg.revert),
      revert: arg.revert,
      onError: setCalendarActionError,
    });
  };

  const renderEventContent = (arg: EventContentArg) => {
    const isCancelled = Boolean(arg.event.extendedProps.isCancelled);
    const bookedCount = Number(arg.event.extendedProps.bookedCount ?? 0);
    const maxParticipants = Number(
      arg.event.extendedProps.maxParticipants ?? 0,
    );
    const trainerName = String(arg.event.extendedProps.trainerName ?? "");

    return (
      <div className="space-y-0.5 px-1">
        <p className="truncate text-xs font-semibold">{arg.event.title}</p>
        {!isCancelled && trainerName && (
          <p className="truncate text-[11px]">{trainerName}</p>
        )}
        {!isCancelled && (
          <p className="text-[11px]">
            {bookedCount}/{maxParticipants}
          </p>
        )}
        {isCancelled && (
          <p className="text-[11px] font-medium uppercase tracking-wide">
            Storniert
          </p>
        )}
      </div>
    );
  };

  const activeTemplate = useMemo(
    () =>
      courses.find((course) => course.id === resolvedTemplateCourseId) ?? null,
    [courses, resolvedTemplateCourseId],
  );

  const editingSessionConfirmedBookings = editingSession
    ? (bookingCounts[editingSession.id] ?? 0)
    : 0;
  const deleteSessionLabel = sessionToDelete
    ? `${sessionToDelete.courses?.name ?? "Kurs"} am ${formatDateTime(sessionToDelete.starts_at)}`
    : "";

  const handleDatesSet = (arg: DatesSetArg) => {
    setVisibleRange({
      from: format(arg.start, "yyyy-MM-dd"),
      to: format(arg.end, "yyyy-MM-dd"),
    });
    setCurrentCalendarView(arg.view.type as "timeGridDay" | "timeGridWeek");
    setCalendarTitle(getCalendarToolbarTitle(arg));
  };

  const navigateCalendar = (direction: "prev" | "next") => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    if (direction === "next") {
      calendarApi.next();
      return;
    }
    calendarApi.prev();
  };

  const goToToday = () => {
    calendarRef.current?.getApi().today();
  };

  const changeCalendarView = (nextView: string) => {
    calendarRef.current?.getApi().changeView(nextView);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div>
        <div>
          <h1 className="text-2xl font-semibold">Termine</h1>
          {showTemplateSidebar && (
            <p className="text-sm text-muted-foreground">
              {isTrainerMode
                ? "Du siehst alle Termine. Bearbeiten kannst du nur Termine deiner zugeordneten Kurse."
                : "Leistungsfähiger Wochenkalender mit Drag-and-Drop. Ziehe Kursblöcke in den Kalender oder verschiebe und resize bestehende Termine direkt per Maus."}
            </p>
          )}
        </div>
      </div>
      <ResourceSelectionHeader
        categories={categoriesWithResources}
        selectedResourceId={selectedResourceId}
        onSelect={setSelectedResourceId}
      />

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        {showTemplateSidebar && (
          <Card className="xl:flex xl:min-h-0 xl:flex-col">
            <CardHeader>
              <CardTitle className="text-lg">Block-Vorlagen</CardTitle>
            </CardHeader>
            <CardContent
              ref={templatesRef}
              className="space-y-2 xl:min-h-0 xl:flex-1 xl:overflow-y-auto"
            >
              {!isTrainerMode && (
                <>
                  <div className="flex flex-col gap-2 mb-4">
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => setSaveTemplateConfirmOpen(true)}
                    >
                      <SaveIcon className="h-4 w-4" />
                      Woche als Template speichern
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => setApplyTemplateConfirmOpen(true)}
                    >
                      <CopyIcon className="h-4 w-4" />
                      Template anwenden
                    </Button>
                  </div>
                  <Separator className="mb-4" />
                </>
              )}
              {templateCourses.map((course) => {
                const isResolvedActive = resolvedTemplateCourseId === course.id;
                const isInactiveCourse = !course.is_active;

                return (
                  <button
                    key={course.id}
                    type="button"
                    className={`session-template w-full rounded-lg border p-3 text-left transition ${
                      isResolvedActive
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:border-primary/50"
                    } ${isInactiveCourse ? "opacity-75" : ""}`}
                    data-template-course-id={course.id}
                    data-template-title={course.name}
                    data-template-duration={String(course.duration_minutes)}
                    onClick={() =>
                      setActiveTemplateCourseId((prev) =>
                        prev === course.id ? "" : course.id,
                      )
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-3 w-3 rounded-full border border-border"
                          style={{ backgroundColor: course.color }}
                          aria-hidden
                        />
                        <p className="truncate font-medium">{course.name}</p>
                      </div>
                      {isInactiveCourse && (
                        <span className="shrink-0 rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-zinc-700">
                          Inaktiv
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Blockdauer: {course.duration_minutes} Minuten
                    </p>
                    {isInactiveCourse && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Dieser Kurs ist aktuell deaktiviert.
                      </p>
                    )}
                  </button>
                );
              })}

              {templateCourses.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {isTrainerMode
                    ? "Dir sind aktuell keine Kurse zugeordnet, die du verwalten kannst."
                    : "Lege zuerst mindestens einen Kurs an."}
                </p>
              )}

              {activeTemplate && (
                <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
                  Aktive Vorlage: <strong>{activeTemplate.name}</strong> (
                  {activeTemplate.duration_minutes} Min)
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="flex min-h-0 flex-col">
          <CardContent className="motionlab-calendar min-h-0 flex-1 overflow-hidden p-0 sm:p-2">
            {showMobileToolbar && (
              <MobileCalendarToolbar
                currentView={currentCalendarView}
                title={calendarTitle}
                views={calendarViews}
                onPrev={() => navigateCalendar("prev")}
                onNext={() => navigateCalendar("next")}
                onToday={goToToday}
                onViewChange={changeCalendarView}
              />
            )}
            <div className="h-full min-h-0">
              <FullCalendar
                ref={calendarRef}
                key={`${calendarView}-${visibleDays}-${dataUpdateCount}`}
                plugins={[timeGridPlugin, interactionPlugin]}
                locale={deLocale}
                initialView={calendarView}
                initialDate={visibleRange.from}
                visibleRange={undefined} // Let FullCalendar handle it based on duration/view
                duration={visibleDays !== 7 ? { days: visibleDays } : undefined}
                scrollTime={DEFAULT_SCROLL_TIME}
                scrollTimeReset={false}
                headerToolbar={
                  showMobileToolbar
                    ? false
                    : {
                      left: "prev,next today",
                      center: "title",
                      right: "timeGridDay,timeGridWeek",
                    }
                }
                buttonText={{
                  today: "Heute",
                  day: "Tag",
                  week: "Woche",
                }}
                weekends
                nowIndicator
                editable
                droppable={templateCourses.length > 0}
                eventDurationEditable
                eventResizableFromStart
                allDaySlot={false}
                slotMinTime={START_TIME}
                slotMaxTime={END_TIME}
                slotDuration={SLOT_DURATION}
                slotLabelInterval={SLOT_DURATION}
                snapDuration={SLOT_DURATION}
                height="100%"
                expandRows
                eventOverlap
                dayHeaderFormat={
                  windowWidth < MOBILE_CALENDAR_BREAKPOINT
                    ? { day: "2-digit", month: "2-digit" }
                    : { weekday: "short", day: "2-digit", month: "2-digit" }
                }
                events={calendarEvents}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                eventReceive={handleEventReceive}
                eventDrop={handleEventDrop}
                eventResize={handleEventResize}
                eventContent={renderEventContent}
                datesSet={handleDatesSet}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {sessionsQuery.isError && (
        <p className="text-sm text-destructive">
          {getErrorMessage(sessionsQuery.error)}
        </p>
      )}
      {bookingCountsQuery.isError && (
        <p className="text-sm text-destructive">
          {getErrorMessage(bookingCountsQuery.error)}
        </p>
      )}
      {!isTrainerMode && trainersQuery.isError && (
        <p className="text-sm text-destructive">
          {getErrorMessage(trainersQuery.error)}
        </p>
      )}
      {profileQuery.isError && (
        <p className="text-sm text-destructive">
          {getErrorMessage(profileQuery.error)}
        </p>
      )}
      {calendarActionError && (
        <p className="text-sm text-destructive">{calendarActionError}</p>
      )}

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setEditingSession(null);
            saveMutation.reset();
            sessionForm.reset(emptyForm);
          }
        }}
      >
        <DialogContent className="max-h-[90dvh] w-[calc(100%-1rem)] max-w-2xl overflow-x-hidden overflow-y-auto p-4 sm:w-full sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {editingSession ? "Termindetails" : "Termin hinzufügen"}
            </DialogTitle>
            <DialogDescription>
              {editingSession
                ? "Termindaten bearbeiten oder die bestätigten Teilnehmer dieses Termins einsehen."
                : isTrainerMode
                  ? "Kurs auswählen, Startzeit setzen und den Termin speichern. Der Trainer wird serverseitig automatisch gesetzt."
                  : "Kurs und optional Trainer auswählen, Startzeit setzen und den Block als Termin speichern."}
            </DialogDescription>
          </DialogHeader>

          <Tabs
            key={editingSession?.id ?? "create-session"}
            defaultValue="edit"
            className="min-w-0 w-full"
          >
            {editingSession && (
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="edit">Termin bearbeiten</TabsTrigger>
                <TabsTrigger value="participants">Teilnehmer</TabsTrigger>
              </TabsList>
            )}

            <TabsContent
              value="edit"
              className={`${editingSession ? "mt-4" : "mt-0"} min-w-0 max-w-full`}
            >
              <Form {...sessionForm}>
                <form
                  onSubmit={onSubmit}
                  className="grid min-w-0 max-w-full grid-cols-[minmax(0,1fr)] gap-4 py-2 [&>*]:min-w-0 [&_input]:min-w-0 [&_input]:max-w-full"
                >
              <FormField
                control={sessionForm.control}
                name="course_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kurs</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        const selected = courses.find(
                          (course) => course.id === value,
                        );
                        const assignedTrainerIds =
                          getAssignedTrainerIdsForCourse(value, courses);
                        const activeAssignedTrainerIds =
                          getActiveAssignedTrainerIdsForCourse(
                            value,
                            courses,
                            activeTrainerIds,
                          );
                        const previousTrainerId =
                          sessionForm.getValues("trainer_id");

                        field.onChange(value);
                        sessionForm.clearErrors("trainer_id");

                        const nextTrainerId = activeTrainerIds.has(
                          previousTrainerId,
                        )
                          ? previousTrainerId
                          : assignedTrainerIds.length === 1 &&
                            activeAssignedTrainerIds.length === 1
                            ? activeAssignedTrainerIds[0]
                            : "";
                        sessionForm.setValue("trainer_id", nextTrainerId, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });

                        if (!editingSession) {
                          sessionForm.setValue(
                            "max_participants",
                            String(
                              selected?.default_max_participants ??
                              Number(FALLBACK_MAX_PARTICIPANTS),
                            ),
                            { shouldDirty: true, shouldValidate: true },
                          );
                          sessionForm.setValue(
                            "min_participants",
                            selected?.default_min_participants == null
                              ? ""
                              : String(selected.default_min_participants),
                            { shouldDirty: true, shouldValidate: true },
                          );
                        }
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Kurs wählen" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {formSelectableCourses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.name} ({course.duration_minutes} Min)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isTrainerMode && (
                <FormField
                  control={sessionForm.control}
                  name="trainer_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Trainer (optional)</FormLabel>
                      <Select
                        value={field.value || "none"}
                        onValueChange={(value) => {
                          field.onChange(value === "none" ? "" : value);
                          sessionForm.clearErrors("trainer_id");
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Trainer wählen (optional)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Kein Trainer</SelectItem>
                          {selectableTrainers.map((trainer) => (
                            <SelectItem key={trainer.id} value={trainer.id}>
                              {trainer.full_name ?? trainer.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={sessionForm.control}
                name="resource_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ressource (optional)</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Ressource wählen (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Keine Ressource</SelectItem>
                        {resourceCategories.map((cat) => (
                          <div key={cat.id}>
                            <div className="px-2 py-1 text-[10px] font-bold uppercase text-muted-foreground">
                              {cat.name}
                            </div>
                            {resources
                              .filter(
                                (resource) => resource.category_id === cat.id,
                              )
                              .map((resource) => (
                                <SelectItem
                                  key={resource.id}
                                  value={resource.id}
                                >
                                  {resource.name}
                                </SelectItem>
                              ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={sessionForm.control}
                name="starts_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="starts-at">Start</FormLabel>
                    <FormControl>
                      <Input
                        id="starts-at"
                        type="datetime-local"
                        className="session-date-time-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <Label>Ende (automatisch aus Kursdauer)</Label>
                <Input
                  value={
                    calculatedEndsAt ? formatDateTime(calculatedEndsAt) : "-"
                  }
                  readOnly
                  disabled
                />
              </div>

              <FormField
                control={sessionForm.control}
                name="registration_until"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="registration-until">
                      Anmeldung möglich bis
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="registration-until"
                        type="datetime-local"
                        className="session-date-time-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={sessionForm.control}
                name="cancellation_until"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="cancellation-until">
                      Stornierung möglich bis
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="cancellation-until"
                        type="datetime-local"
                        className="session-date-time-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={sessionForm.control}
                name="min_participants"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="min-participants">
                      Mindest-Teilnehmer (optional)
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="min-participants"
                        type="number"
                        min={1}
                        placeholder="Optional"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={sessionForm.control}
                name="max_participants"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="max-participants">
                      Max Teilnehmer
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="max-participants"
                        type="number"
                        min={1}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={sessionForm.control}
                name="is_cancelled"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={field.value ? "cancelled" : "active"}
                      onValueChange={(value) =>
                        field.onChange(value === "cancelled")
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Aktiv</SelectItem>
                        <SelectItem value="cancelled">Storniert</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {editingSession && (
                <p className="text-xs text-muted-foreground">
                  Löschen ist nur möglich, wenn für den Termin keine aktiven
                  Buchungen existieren. Aktive Buchungen:{" "}
                  {editingSessionConfirmedBookings}
                </p>
              )}

              {editingSession?.report && (
                <>
                  <div className="py-2">
                    <Separator />
                  </div>
                  <div className="space-y-4 rounded-lg border border-muted bg-muted/40 p-3">
                    <h4 className="text-sm font-semibold">
                      Sitzungsbericht (Trainer)
                    </h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase text-muted-foreground">
                          Gehaltene Minuten
                        </Label>
                        <p className="text-sm font-medium">
                          {editingSession.report.actual_duration_minutes} min
                        </p>
                      </div>
                      {editingSession.report.substitute_trainer && (
                        <div className="space-y-1">
                          <Label className="text-[10px] font-bold uppercase text-muted-foreground">
                            Vertretung
                          </Label>
                          <p className="text-sm font-medium">
                            {editingSession.report.substitute_trainer.full_name}
                          </p>
                        </div>
                      )}
                    </div>

                    {editingSession.report.trainer_notes && (
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase text-muted-foreground">
                          Trainer Notizen
                        </Label>
                        <p className="whitespace-pre-wrap text-sm italic text-muted-foreground">
                          {editingSession.report.trainer_notes}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}

              <DialogFooter>
                {editingSession && (
                  <div className="flex flex-1 items-center gap-2">
                    <Button
                      type="button"
                      variant="destructive"
                      className="w-full sm:w-auto"
                      onClick={() => openDeleteConfirm(editingSession)}
                      disabled={deleteMutation.isPending}
                    >
                      Termin löschen
                    </Button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Speichere ..." : "Speichern"}
                </Button>
              </DialogFooter>
                </form>
              </Form>
            </TabsContent>

            {editingSession && (
              <TabsContent value="participants" className="mt-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold">
                    Teilnehmer ({confirmedSessionBookings.length} /{" "}
                    {editingSession.max_participants})
                  </h3>
                </div>

                {sessionBookingsQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : sessionBookingsQuery.isError ? (
                  <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
                    {getErrorMessage(sessionBookingsQuery.error)}
                  </div>
                ) : confirmedSessionBookings.length > 0 ? (
                  <>
                    <div className="space-y-2 md:hidden">
                      {confirmedSessionBookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded-lg border px-3 py-2.5"
                        >
                          <div className="min-w-0">
                            <p className="flex items-center font-medium">
                              <span className="truncate">{booking.full_name}</span>
                              <BirthdayIcon visible={booking.has_birthday_on_session_date} />
                            </p>
                            <p className="truncate text-sm text-muted-foreground">
                              {booking.email}
                            </p>
                          </div>
                          {booking.booked_by_user_id ? (
                            <Button size="sm" variant="outline" className="h-7 px-2.5" asChild>
                              <Link
                                to={`${isTrainerMode ? "/trainer" : "/admin"}/users/${booking.booked_by_user_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Details
                              </Link>
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    <Table containerClassName="hidden max-h-[400px] md:block">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>E-Mail</TableHead>
                          <TableHead className="text-right">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {confirmedSessionBookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell className="font-medium">
                              {booking.full_name}
                              <BirthdayIcon visible={booking.has_birthday_on_session_date} />
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {booking.email}
                            </TableCell>
                            <TableCell className="text-right">
                              {booking.booked_by_user_id ? (
                                <Button size="sm" variant="outline" asChild>
                                  <Link
                                    to={`${isTrainerMode ? "/trainer" : "/admin"}/users/${booking.booked_by_user_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Details
                                  </Link>
                                </Button>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  -
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <div className="rounded-md border py-12 text-center text-sm text-muted-foreground">
                    Noch keine Teilnehmer für diesen Termin.
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(nextOpen) => {
          setDeleteConfirmOpen(nextOpen);
          if (!nextOpen && !deleteMutation.isPending) {
            setSessionToDelete(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Termin löschen?</DialogTitle>
            <DialogDescription>
              Dieser Termin wird endgültig gelöscht.
            </DialogDescription>
          </DialogHeader>

          {deleteSessionLabel && (
            <p className="text-sm text-muted-foreground">
              {deleteSessionLabel}
            </p>
          )}

          {sessionToDelete && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Aktive Buchungen (bestätigt/ausstehend):{" "}
                {bookingCounts[sessionToDelete.id] ?? 0}
              </p>

              {(bookingCounts[sessionToDelete.id] ?? 0) > 0 && (
                <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
                  Der Termin kann nicht gelöscht werden, da noch aktive
                  Buchungen existieren. Bitte storniere zuerst alle Teilnahmen
                  (bookings) oder setze den Termin auf 'Storniert'.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setSessionToDelete(null);
              }}
              disabled={deleteMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (!sessionToDelete) {
                  return;
                }

                deleteMutation.mutate(sessionToDelete.id);
              }}
              disabled={
                deleteMutation.isPending ||
                !sessionToDelete ||
                (bookingCounts[sessionToDelete.id] ?? 0) > 0
              }
            >
              {deleteMutation.isPending ? "Lösche ..." : "Ja, löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation confirmation: all bookings of the session are cancelled and refunded */}
      <Dialog
        open={pendingCancellation !== null && editingSession !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingCancellation(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Termin absagen?</DialogTitle>
            {editingSession && (
              <DialogDescription>
                {editingSession.courses?.name ?? "Kurs"} am {formatDateTime(editingSession.starts_at)}
              </DialogDescription>
            )}
          </DialogHeader>

          {editingSession && (
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Bestätigte Buchungen: <strong className="text-foreground">{bookingCounts[editingSession.id] ?? 0}</strong>
              </p>
              <p>
                Alle Buchungen und Gastteilnahmen dieses Termins werden storniert, die Credits erstattet und die
                Teilnehmer per E-Mail informiert.
              </p>
              <p>Wird der Termin später wieder aktiv gesetzt, bleiben die Buchungen storniert.</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPendingCancellation(null)}>
              Zurück
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={saveMutation.isPending}
              onClick={() => {
                if (pendingCancellation) saveMutation.mutate(pendingCancellation);
                setPendingCancellation(null);
              }}
            >
              Termin absagen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move confirmation: past target and/or affected members */}
      <Dialog
        open={pendingMove !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && pendingMove) {
            pendingMove.revert();
            setPendingMove(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingMove?.isPast
                ? "Termin in die Vergangenheit verschieben?"
                : "Termin trotzdem verschieben?"}
            </DialogTitle>
            <DialogDescription>
              {pendingMove?.isPast
                ? "Der Termin wird in die Vergangenheit verschoben. Eine Buchung durch Teilnehmer ist dann nicht mehr möglich."
                : "Für einige Teilnehmer deckt das Abo den neuen Termin nicht regulär ab."}
            </DialogDescription>
          </DialogHeader>

          {pendingMove && (
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="space-y-1">
                <p>
                  <strong>{pendingMove.session.courses?.name ?? "Kurs"}</strong>
                </p>
                <p>
                  Neuer Zeitpunkt:{" "}
                  {formatDateTime(pendingMove.startsAt.toISOString())}
                </p>
                <p>
                  Bestätigte Buchungen:{" "}
                  {bookingCounts[pendingMove.session.id] ?? 0}
                </p>
              </div>
              {pendingMove.conflicts.length > 0 && (
                <div className="space-y-1">
                  <p className="font-medium text-foreground">
                    Betroffene Mitglieder
                  </p>
                  <ul className="space-y-1">
                    {pendingMove.conflicts.map((conflict) => (
                      <li key={conflict.booking_id}>
                        <strong>{conflict.name ?? "Mitglied"}</strong>:{" "}
                        {SESSION_MOVE_REASON_LABELS[conflict.reason] ??
                          conflict.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                pendingMove?.revert();
                setPendingMove(null);
              }}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (!pendingMove) return;
                const move = pendingMove;
                setPendingMove(null);
                await move.proceed();
              }}
            >
              {pendingMove && pendingMove.conflicts.length > 0
                ? "Trotzdem verschieben"
                : "Ja, verschieben"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template confirmation dialog */}
      <Dialog
        open={saveTemplateConfirmOpen}
        onOpenChange={setSaveTemplateConfirmOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Woche als Template speichern?</DialogTitle>
            <DialogDescription>
              Die aktuelle Woche wird als neue Vorlage gespeichert. Ein
              vorhandenes Template wird dabei überschrieben.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveTemplateConfirmOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => saveTemplateMutation.mutate()}
              disabled={saveTemplateMutation.isPending}
            >
              {saveTemplateMutation.isPending ? "Speichere..." : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Template confirmation dialog */}
      <Dialog
        open={applyTemplateConfirmOpen}
        onOpenChange={setApplyTemplateConfirmOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Template auf diese Woche anwenden?</DialogTitle>
            <DialogDescription>
              Alle Termine aus dem gespeicherten Template werden in die
              aktuelle Woche kopiert.
            </DialogDescription>
          </DialogHeader>

          {!isTrainerMode && (
            <div className="flex items-center space-x-2 py-4">
              <Checkbox
                id="overwrite-checkbox"
                checked={overwriteTemplate}
                onCheckedChange={(checked) => setOverwriteTemplate(!!checked)}
              />
              <label
                htmlFor="overwrite-checkbox"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Bestehende Termine in dieser Woche vorher löschen?
              </label>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApplyTemplateConfirmOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => applyTemplateMutation.mutate()}
              disabled={applyTemplateMutation.isPending}
            >
              {applyTemplateMutation.isPending ? "Wende an..." : "Anwenden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
